/**
 * MobileSubcontractorDetailsModal
 *
 * Mobile full-screen MUI Dialog replacement for SubcontractorDetailsModal.
 * Slide-up transition, sticky AppBar, tabbed sections (Info / Dates / Documents),
 * sticky bottom save button. All inputs use fontSize 16px to prevent iOS zoom.
 *
 * All components: MUI (@mui/material, @mui/icons-material)
 */
import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
  Slide,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAsset,
  downloadAsset,
  getSubcontractorAssets,
  uploadAsset,
} from "../../../assets/assetsSlice";
import {
  editSubcontractor,
  getSubcontractors,
} from "../../../components/clients/clientsSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const inputSx = {
  "& input": { fontSize: "16px" },
  "& textarea": { fontSize: "16px" },
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "rgba(255,255,255,0.5)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    transition: TRANSITION_SMOOTH,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
    "& fieldset": {
      borderColor: "rgba(255,144,21,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": { borderColor: "rgba(255,144,21,0.2)" },
    "&.Mui-focused fieldset": {
      borderColor: "#FF9015",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
    },
    "&.Mui-focused": {
      bgcolor: "rgba(255,255,255,0.7)",
      boxShadow: "0 4px 16px rgba(255,144,21,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#FF9015" },
};

const selectSx = {
  borderRadius: "14px",
  fontSize: "16px",
  bgcolor: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(12px)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
  transition: TRANSITION_SMOOTH,
  "& fieldset": { borderColor: "rgba(255,144,21,0.08)" },
  "&:hover fieldset": { borderColor: "rgba(255,144,21,0.2)" },
  "&.Mui-focused fieldset": {
    borderColor: "#FF9015",
    boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
  },
};

/* ------------------------------------------------------------------ */
/*  Date fields config                                                 */
/* ------------------------------------------------------------------ */
const DATE_FIELDS = [
  { key: "date_attestation_urssaf", labelKey: "DATE_ATTESTATION_URSSAF", disabled: false },
  { key: "date_fin_attestation_urssaf", labelKey: "DATE_FIN_ATTESTATION_URSSAF", disabled: true },
  { key: "date_attestation_fiscale", labelKey: "DATE_ATTESTATION_FISCALE", disabled: false },
  { key: "date_fin_attestation_fiscale", labelKey: "DATE_FIN_ATTESTATION_FISCALE", disabled: true },
  { key: "date_fin_validite_rc", labelKey: "DATE_FIN_VALIDITE_RC", disabled: false },
  { key: "date_attestation_salarie_etranger", labelKey: "DATE_ATTESTATION_SALARIE_ETRANGER", disabled: false },
  { key: "date_fin_attestation_salarie_etranger", labelKey: "DATE_FIN_ATTESTATION_SALARIE_ETRANGER", disabled: true },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param {Object}   props
 * @param {boolean}  props.visible - controls dialog open state
 * @param {Function} props.setVisible - toggle visibility
 * @param {Object}   props.subcontractor - selected subcontractor data
 * @param {Function} props.setSubcontractor - clear selected subcontractor
 */
export default function MobileSubcontractorDetailsModal({
  visible,
  setVisible,
  subcontractor,
  setSubcontractor,
}) {
  const dispatch = useDispatch();
  const providerTypes = useSelector((state) => state.clients.providerTypes);

  const [activeTab, setActiveTab] = useState(0);
  const [confirmDeleteAsset, setConfirmDeleteAsset] = useState(null);
  const fileInputRef = useRef(null);

  const emptyData = {
    id: null,
    name: null,
    status: null,
    type: null,
    address: null,
    reminders_dates: null,
    reminders_comments: null,
    email: null,
    vfact: null,
    date_attestation_urssaf: null,
    date_fin_attestation_urssaf: null,
    date_attestation_fiscale: null,
    date_fin_attestation_fiscale: null,
    date_fin_validite_rc: null,
    date_attestation_salarie_etranger: null,
    date_fin_attestation_salarie_etranger: null,
    assets: [],
  };

  const [subcontractorData, setSubcontractorData] = useState(emptyData);

  const statusOptions = [
    { name: "Actif", color: "#4caf50" },
    { name: "Inactif", color: "#f44336" },
  ];

  const typeOptions = providerTypes.map((t) => ({ name: t.value }));

  /* ---- Sync data from prop ---- */
  useEffect(() => {
    if (subcontractor) {
      setSubcontractorData({
        ...subcontractor,
        date_attestation_urssaf: subcontractor.date_attestation_urssaf
          ? moment(subcontractor.date_attestation_urssaf).toDate()
          : null,
        date_fin_attestation_urssaf: subcontractor.date_fin_attestation_urssaf
          ? moment(subcontractor.date_fin_attestation_urssaf).toDate()
          : null,
        date_attestation_fiscale: subcontractor.date_attestation_fiscale
          ? moment(subcontractor.date_attestation_fiscale).toDate()
          : null,
        date_fin_attestation_fiscale: subcontractor.date_fin_attestation_fiscale
          ? moment(subcontractor.date_fin_attestation_fiscale).toDate()
          : null,
        date_fin_validite_rc: subcontractor.date_fin_validite_rc
          ? moment(subcontractor.date_fin_validite_rc).toDate()
          : null,
        date_attestation_salarie_etranger:
          subcontractor.date_attestation_salarie_etranger
            ? moment(subcontractor.date_attestation_salarie_etranger).toDate()
            : null,
        date_fin_attestation_salarie_etranger:
          subcontractor.date_fin_attestation_salarie_etranger
            ? moment(
                subcontractor.date_fin_attestation_salarie_etranger
              ).toDate()
            : null,
      });
      setActiveTab(0);
    } else {
      setSubcontractorData(emptyData);
    }
  }, [subcontractor]);

  /* ---- Close handler ---- */
  const handleClose = () => {
    setVisible(false);
    setSubcontractor(null);
    setSubcontractorData(emptyData);
    setActiveTab(0);
    setConfirmDeleteAsset(null);
  };

  /* ---- Field update helper ---- */
  const updateField = (field, value) => {
    setSubcontractorData((prev) => ({ ...prev, [field]: value }));
  };

  /* ---- Save ---- */
  const updateSubcontractor = () => {
    if (
      subcontractorData &&
      subcontractorData.id &&
      subcontractorData.name &&
      subcontractorData.name.trim() !== ""
    ) {
      let toEdit = {
        ...subcontractorData,
        date_attestation_urssaf: subcontractorData.date_attestation_urssaf
          ? moment(subcontractorData.date_attestation_urssaf).format("YYYY-MM-DD")
          : null,
        date_attestation_fiscale: subcontractorData.date_attestation_fiscale
          ? moment(subcontractorData.date_attestation_fiscale).format("YYYY-MM-DD")
          : null,
        date_fin_validite_rc: subcontractorData.date_fin_validite_rc
          ? moment(subcontractorData.date_fin_validite_rc).format("YYYY-MM-DD")
          : null,
        date_attestation_salarie_etranger:
          subcontractorData.date_attestation_salarie_etranger
            ? moment(subcontractorData.date_attestation_salarie_etranger).format(
                "YYYY-MM-DD"
              )
            : null,
      };
      dispatch(editSubcontractor({ subcontractor: toEdit }))
        .then((response) => {
          const responseData = response.payload.responseData;
          if (responseData && responseData.status === 1) {
            showSuccessToast("Fournisseur modifié avec succès");
            dispatch(getSubcontractors());
          }
        })
        .catch((error) => {
          showErrorToast("An error occurred while updating subcontractor.");
          console.error("An error occurred while updating subcontractor:", error);
        });
    } else {
      showErrorToast("Veuillez remplir le nom du Fournisseur");
    }
  };

  /* ---- Asset upload ---- */
  const handleFileUpload = (file) => {
    if (!file) return;
    dispatch(
      uploadAsset({
        file,
        tableName: "subcontractors",
        idParent: subcontractorData.id,
        filename: file.name,
      })
    )
      .then((actionResponse) => {
        if (uploadAsset.fulfilled.match(actionResponse)) {
          dispatch(
            getSubcontractorAssets({ id_subcontractor: subcontractorData.id })
          ).then((ar) => {
            if (getSubcontractorAssets.fulfilled.match(ar)) {
              setSubcontractorData((prev) => ({
                ...prev,
                assets: ar.payload.responseData.assets,
              }));
              dispatch(getSubcontractors());
            }
          });
        }
      })
      .catch((error) => {
        console.error("Error handling in uploadAsset:", error);
      });
  };

  /* ---- Asset delete ---- */
  const handleDeleteAsset = (asset) => {
    dispatch(
      deleteAsset({
        tableName: asset.table_name,
        idParent: asset.id_parent,
        filename: asset.filename,
      })
    )
      .then((actionResponse) => {
        if (deleteAsset.fulfilled.match(actionResponse)) {
          showSuccessToast("Fichier supprimé avec succès");
          dispatch(
            getSubcontractorAssets({ id_subcontractor: subcontractorData.id })
          ).then((ar) => {
            if (getSubcontractorAssets.fulfilled.match(ar)) {
              setSubcontractorData((prev) => ({
                ...prev,
                assets: ar.payload.responseData.assets,
              }));
              dispatch(getSubcontractors());
            }
          });
        }
      })
      .catch((error) => {
        console.error("Error handling in deleteAsset:", error);
      });
    setConfirmDeleteAsset(null);
  };

  /* ---- Asset download ---- */
  const handleDownloadAsset = (asset) => {
    dispatch(
      downloadAsset({
        tableName: asset.table_name,
        idParent: asset.id_parent,
        filename: asset.filename,
      })
    );
  };

  /* ---- Date helper ---- */
  const dateToInputValue = (dateVal) => {
    if (!dateVal) return "";
    return moment(dateVal).format("YYYY-MM-DD");
  };

  const isDateExpired = (dateVal, fieldKey) => {
    if (!dateVal || !fieldKey.includes("fin")) return false;
    return new Date(dateVal) < new Date();
  };

  /* ---------------------------------------------------------------- */
  /*  Tab 0: Info                                                      */
  /* ---------------------------------------------------------------- */
  const renderInfoTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      <TextField
        label={<Langs str="Nom" />}
        fullWidth
        size="small"
        value={subcontractorData.name || ""}
        onChange={(e) => updateField("name", e.target.value)}
        sx={inputSx}
      />

      <FormControl fullWidth size="small">
        <InputLabel sx={{ "&.Mui-focused": { color: "#FF9015" } }}>
          <Langs str="STATUS" />
        </InputLabel>
        <Select
          value={subcontractorData.status || ""}
          label={<Langs str="STATUS" />}
          onChange={(e) => updateField("status", e.target.value)}
          sx={selectSx}
          MenuProps={{ sx: { zIndex: 1500 } }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.name} value={opt.name}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: opt.color,
                  }}
                />
                {opt.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel sx={{ "&.Mui-focused": { color: "#FF9015" } }}>
          <Langs str="TYPE" />
        </InputLabel>
        <Select
          value={subcontractorData.type || ""}
          label={<Langs str="TYPE" />}
          onChange={(e) => updateField("type", e.target.value)}
          sx={selectSx}
          MenuProps={{ sx: { zIndex: 1500 } }}
        >
          {typeOptions.map((opt) => (
            <MenuItem key={opt.name} value={opt.name}>
              {opt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label={<Langs str="ADDRESS" />}
        fullWidth
        size="small"
        value={subcontractorData.address || ""}
        onChange={(e) => updateField("address", e.target.value)}
        sx={inputSx}
      />

      <TextField
        label={<Langs str="EMAIL" />}
        fullWidth
        size="small"
        type="email"
        value={subcontractorData.email || ""}
        onChange={(e) => updateField("email", e.target.value)}
        sx={inputSx}
      />

      <TextField
        label={<Langs str="vFact" />}
        fullWidth
        size="small"
        value={subcontractorData.vfact || ""}
        onChange={(e) => updateField("vfact", e.target.value)}
        sx={inputSx}
      />

      <TextField
        label={<Langs str="REMINDERS_DATES" />}
        fullWidth
        size="small"
        multiline
        minRows={2}
        maxRows={6}
        value={subcontractorData.reminders_dates || ""}
        onChange={(e) => updateField("reminders_dates", e.target.value)}
        sx={inputSx}
      />

      <TextField
        label={<Langs str="REMINDERS_COMMENTS" />}
        fullWidth
        size="small"
        multiline
        minRows={2}
        maxRows={6}
        value={subcontractorData.reminders_comments || ""}
        onChange={(e) => updateField("reminders_comments", e.target.value)}
        sx={inputSx}
      />
    </Box>
  );

  /* ---------------------------------------------------------------- */
  /*  Tab 1: Dates                                                     */
  /* ---------------------------------------------------------------- */
  const renderDatesTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      {DATE_FIELDS.map(({ key, labelKey, disabled }) => {
        const dateVal = subcontractorData[key];
        const expired = isDateExpired(dateVal, key);

        return (
          <Box key={key}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.78rem",
                color: expired ? "#ed6c02" : "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              {expired && (
                <WarningAmberOutlinedIcon sx={{ fontSize: "0.9rem" }} />
              )}
              {Localize(labelKey)}
            </Typography>
            <TextField
              type="date"
              fullWidth
              size="small"
              disabled={disabled}
              value={dateToInputValue(dateVal)}
              onChange={(e) => {
                const val = e.target.value;
                updateField(key, val ? new Date(val) : null);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...inputSx,
                "& .MuiOutlinedInput-root": {
                  ...inputSx["& .MuiOutlinedInput-root"],
                  ...(expired && !disabled
                    ? {
                        "& fieldset": {
                          borderColor: "rgba(237,108,2,0.4)",
                        },
                      }
                    : {}),
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );

  /* ---------------------------------------------------------------- */
  /*  Tab 2: Documents                                                 */
  /* ---------------------------------------------------------------- */
  const renderDocumentsTab = () => {
    const assets = subcontractorData.assets || [];

    return (
      <Box sx={{ p: 2 }}>
        {/* Upload button */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            mb: 2,
            minHeight: 48,
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            borderColor: "#FF9015",
            color: "#FF9015",
            "&:hover": {
              borderColor: "#FF9015",
              bgcolor: "rgba(255,144,21,0.06)",
            },
            "&:active": { transform: "scale(0.97)" },
          }}
        >
          <Langs str="Upload" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            handleFileUpload(e.target.files[0]);
            e.target.value = "";
          }}
        />

        {/* Asset list */}
        {assets.length === 0 && (
          <Fade in timeout={300}>
            <Box sx={{ textAlign: "center", py: 6 }}>
              <DescriptionOutlinedIcon
                sx={{ fontSize: 48, color: "rgba(0,0,0,0.12)", mb: 1 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                {Localize("NO_DOCUMENTS")}
              </Typography>
            </Box>
          </Fade>
        )}

        {assets.map((asset, index) => (
          <Fade in key={index} timeout={200 + index * 50}>
            <Card
              elevation={0}
              sx={{
                mb: 1,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(0,0,0,0.06)",
                transition: TRANSITION_SMOOTH,
              }}
            >
              <CardContent
                sx={{
                  p: 1.5,
                  "&:last-child": { pb: 1.5 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <DescriptionOutlinedIcon
                  sx={{ fontSize: 20, color: "rgba(0,0,0,0.4)", flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {asset.filename}
                </Typography>
                <IconButton
                  onClick={() => handleDownloadAsset(asset)}
                  size="small"
                  sx={{
                    minWidth: 44,
                    minHeight: 44,
                    borderRadius: "10px",
                    color: "#FF9015",
                    transition: TRANSITION_SMOOTH,
                    "&:active": { transform: "scale(0.92)" },
                  }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>

                {/* Delete button with confirm */}
                {confirmDeleteAsset === index ? (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      onClick={() => handleDeleteAsset(asset)}
                      size="small"
                      sx={{
                        minWidth: 40,
                        minHeight: 40,
                        borderRadius: "10px",
                        bgcolor: "#f44336",
                        color: "#fff",
                        "&:hover": { bgcolor: "#d32f2f" },
                        "&:active": { transform: "scale(0.92)" },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => setConfirmDeleteAsset(null)}
                      size="small"
                      sx={{
                        minWidth: 40,
                        minHeight: 40,
                        borderRadius: "10px",
                        bgcolor: "rgba(0,0,0,0.06)",
                        color: "rgba(0,0,0,0.6)",
                        "&:active": { transform: "scale(0.92)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <IconButton
                    onClick={() => setConfirmDeleteAsset(index)}
                    size="small"
                    sx={{
                      minWidth: 44,
                      minHeight: 44,
                      borderRadius: "10px",
                      color: "rgba(0,0,0,0.4)",
                      transition: TRANSITION_SMOOTH,
                      "&:active": { transform: "scale(0.92)" },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </CardContent>
            </Card>
          </Fade>
        ))}
      </Box>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          backgroundColor: "rgba(245,246,250,0.97)",
          backdropFilter: "blur(24px)",
        },
      }}
    >
      {/* Sticky AppBar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          borderBottom: "1px solid rgba(0,0,0,0.04)",
          color: "rgba(0,0,0,0.87)",
          boxShadow: "0 4px 24px -4px rgba(0,0,0,0.04)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: "10%",
            right: "10%",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.2), transparent)",
          },
        }}
      >
        <Toolbar sx={{ minHeight: 56, px: 1 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            sx={{
              minWidth: 44,
              minHeight: 44,
              borderRadius: "12px",
              color: "rgba(0,0,0,0.7)",
              transition: TRANSITION_SMOOTH,
              "&:active": { transform: "scale(0.94)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, ml: 0.5, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.05rem",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {subcontractorData.name || Localize("SUBCONTRACTORS")}
            </Typography>
            {subcontractorData.status && (
              <Chip
                label={subcontractorData.status}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  borderRadius: "6px",
                  color: "#fff",
                  bgcolor:
                    subcontractorData.status === "Actif" ? "#4caf50" : "#f44336",
                  mt: 0.25,
                }}
              />
            )}
          </Box>
        </Toolbar>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val);
            setConfirmDeleteAsset(null);
          }}
          variant="fullWidth"
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              transition: TRANSITION_SMOOTH,
            },
            "& .MuiTabs-indicator": {
              background: "linear-gradient(90deg, #FF8701, #FFC100)",
              height: 3,
              borderRadius: "3px 3px 0 0",
              boxShadow: "0 0 12px rgba(255,144,21,0.3)",
              transition: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
            "& .Mui-selected": {
              color: "#FF9015 !important",
              textShadow: "0 0 20px rgba(255,144,21,0.15)",
            },
          }}
        >
          <Tab label={Localize("INFORMATION")} />
          <Tab label="Dates" />
          <Tab label="Documents" />
        </Tabs>
      </AppBar>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 12,
        }}
      >
        {activeTab === 0 && renderInfoTab()}
        {activeTab === 1 && renderDatesTab()}
        {activeTab === 2 && renderDocumentsTab()}
      </Box>

      {/* Sticky bottom save button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          pb: 3,
          bgcolor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          zIndex: 1,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={updateSubcontractor}
          sx={{
            minHeight: 48,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.95rem",
            background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
            boxShadow: "0 6px 20px rgba(255,144,21,0.35), 0 2px 6px rgba(255,144,21,0.15)",
            "&:hover": {
              background: "linear-gradient(135deg, #ff7b00 0%, #FF9015 100%)",
              boxShadow: "0 8px 28px rgba(255,144,21,0.4)",
            },
            "&:active": {
              transform: "scale(0.97)",
            },
          }}
        >
          <Langs str="SAVE" />
        </Button>
      </Box>
    </Dialog>
  );
}
